import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { DashboardLayout, TierBadge, StatusBadge } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { MUTABLE_CATEGORIES, getCategoryMeta } from '../../lib/notifications';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { ParticularsForm } from '../../components/AgreementSigner';
import ApplicationAnswers from '../../components/ApplicationAnswers';
import { User, CreditCard, Building2, Bell, ClipboardList } from 'lucide-react';
import axios from 'axios';


const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const CATEGORY_DESC = {
  commission: 'Commission earned and payouts',
  lead: 'Updates on your referred leads',
  client: 'Updates about your referred clients',
  marketplace: 'Project applications, decisions & replies',
  promo: 'When your promo codes are used',
  partner: 'Tier and partner type changes',
};

// Matches the date style used on Payouts and Leads.
const formatAcceptedAt = (str) =>
  str ? new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PartnerProfile = () => {
  const { user, getAuthHeaders, refreshUser, API_URL } = useAuth();
  const { updatePreferences } = useNotifications();
  const [prefs, setPrefs] = useState(user?.notification_prefs || {});
  const [emailPrefs, setEmailPrefs] = useState(user?.email_prefs || {});
  const [savingPref, setSavingPref] = useState(null);
  const [termsStatus, setTermsStatus] = useState(null);
  // Fetched rather than read off `user`: these are twenty fields nothing else on the session
  // needs, and a form that cannot see its own current values posts blanks over them.
  const [applicationAnswers, setApplicationAnswers] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/auth/terms-status`, { headers: getAuthHeaders() })
      .then(({ data }) => setTermsStatus(data))
      .catch(() => {}); // a display-only line must never break the page
    axios
      .get(`${API_URL}/api/partner/application`, { headers: getAuthHeaders() })
      .then(({ data }) => setApplicationAnswers(data.answers))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPrefs(user?.notification_prefs || {});
    setEmailPrefs(user?.email_prefs || {});
  }, [user]);

  const handleTogglePref = async (category, enabled) => {
    setSavingPref(category);
    setPrefs((prev) => ({ ...prev, [category]: enabled }));
    try {
      await updatePreferences({ [category]: enabled });
      await refreshUser();
      toast.success(`${getCategoryMeta(category).label} notifications ${enabled ? 'enabled' : 'muted'}`);
    } catch (error) {
      setPrefs((prev) => ({ ...prev, [category]: !enabled })); // revert
      toast.error('Failed to update preference');
    } finally {
      setSavingPref(null);
    }
  };

  const handleToggleEmailPref = async (category, enabled) => {
    setSavingPref(`email:${category}`);
    setEmailPrefs((prev) => ({ ...prev, [category]: enabled }));
    try {
      await updatePreferences({ email: { [category]: enabled } });
      await refreshUser();
      toast.success(`${getCategoryMeta(category).label} emails ${enabled ? 'enabled' : 'muted'}`);
    } catch (error) {
      setEmailPrefs((prev) => ({ ...prev, [category]: !enabled })); // revert
      toast.error('Failed to update preference');
    } finally {
      setSavingPref(null);
    }
  };
  const [bankAccount, setBankAccount] = useState(user?.bank_account || '');
  const [upiId, setUpiId] = useState(user?.upi_id || '');
  const [companyNameBank, setCompanyNameBank] = useState(user?.company_name_bank || '');
  const [ifscCode, setIfscCode] = useState(user?.ifsc_code || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBankAccount(user?.bank_account || '');
    setUpiId(user?.upi_id || '');
    setCompanyNameBank(user?.company_name_bank || '');
    setIfscCode(user?.ifsc_code || '');
  }, [user]);


  const handleSavePayment = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/partner/profile`, null, {
        params: {
          bank_account: bankAccount,
          upi_id: upiId,
          company_name_bank: companyNameBank,
          ifsc_code: ifscCode,
        },
        headers: getAuthHeaders()
      });
      await refreshUser();
      toast.success('Payment details updated successfully!');
    } catch (error) {
      // The backend validates IFSC / UPI / account / GSTIN and names the offending field.
      toast.error(error?.response?.data?.detail || 'Failed to update payment details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="partner-profile">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">
            Profile & Settings
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Manage your account details and preferences
          </p>
        </div>

        {/* Five tabs rather than one long column.
            Everything here used to sit in a two-column grid that ran well past the fold, which
            put "where your money lands" and a notification switch on the same screen as the
            partner's own name. These are five separate errands and a partner arrives with one
            of them in mind, so each gets its own tab and nothing scrolls to be found. */}
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="flex-wrap">
            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="business" data-testid="tab-business">Business &amp; legal</TabsTrigger>
            <TabsTrigger value="banking" data-testid="tab-banking">Banking</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">Business overview</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="mt-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-[#ef785b]" />
                Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#FAFAF8] rounded-lg">
                <div className="w-16 h-16 rounded-full bg-[#ef785b]/10 flex items-center justify-center">
                  <span className="text-2xl font-bold text-[#ef785b]">
                    {user?.full_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-lg text-[#1A1A1A]">{user?.full_name}</p>
                  <p className="text-sm text-[#64748B]">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <TierBadge tier={user?.tier} />
                    <StatusBadge status={user?.status} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[#64748B] uppercase tracking-wider">Mobile</Label>
                  <p className="font-medium text-[#1A1A1A]">{user?.mobile}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#64748B] uppercase tracking-wider">City</Label>
                  <p className="font-medium text-[#1A1A1A]">{user?.city}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#64748B] uppercase tracking-wider">Tier</Label>
                  <p className="font-medium text-[#1A1A1A] capitalize">{user?.tier || 'silver'}</p>
                </div>
                <div>
                  <Label className="text-xs text-[#64748B] uppercase tracking-wider">Company</Label>
                  <p className="font-medium text-[#1A1A1A]">{user?.company_name || '—'}</p>
                </div>
              </div>

              {/* What this partner agreed to, and when. Read-only by design — the record is
                  append-only on the server and nothing here can amend it. */}
              <div className="pt-4 border-t border-[#E2E2DF]">
                <Label className="text-xs text-[#64748B] uppercase tracking-wider">Partner terms</Label>
                <p className="text-sm text-[#1A1A1A] mt-1" data-testid="profile-terms">
                  {termsStatus?.accepted_version ? (
                    <>
                      Version {termsStatus.accepted_version} accepted on{' '}
                      {formatAcceptedAt(termsStatus.accepted_at)}
                      {termsStatus.needs_acceptance && (
                        <span className="text-[#ee785b]">
                          {' '}— version {termsStatus.current_version} is now current
                        </span>
                      )}
                    </>
                  ) : (
                    'Not yet accepted'
                  )}
                  {' · '}
                  <Link to="/terms" className="text-[#ee785b] hover:underline">Read the terms</Link>
                </p>
              </div>
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="overview" className="mt-4">
          {/* What they told us when they applied. Editable, because an application is a snapshot
              of the day it was filled in and these are the answers that go stale fastest: an
              agency that grows from four clients to twenty is exactly the partner we most want to
              be right about, and they are the only ones who know. Every change reaches the CRM as
              a `partner_details_updated` event. */}
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="w-5 h-5 text-[#ef785b]" />
                Your business, as we have it
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-5 text-sm text-[#64748B]">
                These are the answers from your application. If anything has changed, change it
                here. Your partner manager sees every update.
              </p>
              {applicationAnswers ? (
                <ApplicationAnswers
                  endpoint={`${API_URL}/api/partner/application`}
                  headers={getAuthHeaders()}
                  answers={applicationAnswers}
                  onSaved={setApplicationAnswers}
                />
              ) : (
                <p className="text-sm text-[#64748B]">Loading your answers…</p>
              )}
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="business" className="mt-4">
            {/* Who you are, in law. ONE form, ONE place — the agreement prints these and so does
                every invoice, and they are the same facts about the same entity. A second copy of
                them on the agreement screen would eventually produce a contract and a bill that
                disagree about the partner's own name. */}
            <Card className="bg-white border-[#E2E2DF]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-[#ef785b]" />
                  Business &amp; legal details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-5 text-sm text-[#64748B]">
                  Your partnership agreement, your invoices and your payouts all print these. Change
                  them here and they change everywhere.
                </p>
                <ParticularsForm
                  initial={user?.particulars}
                  missing={[]}
                  user={user}
                  embedded
                  onSaved={refreshUser}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="banking" className="mt-4">
            <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-[#ef785b]" />
                Where your money lands
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">Company Name (As per Bank)</Label>
                <Input
                  value={companyNameBank}
                  onChange={(e) => setCompanyNameBank(e.target.value)}
                  placeholder="Enter company name as per bank records"
                  className="mt-1.5 h-10 border-[#E2E2DF]"
                  data-testid="company-name-bank"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">Bank Account Number</Label>
                <Input
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  placeholder="Enter bank account number"
                  className="mt-1.5 h-10 border-[#E2E2DF]"
                  data-testid="bank-account"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">IFSC Code</Label>
                <Input
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                  placeholder="e.g. SBIN0001234"
                  className="mt-1.5 h-10 border-[#E2E2DF]"
                  data-testid="ifsc-code"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">UPI ID</Label>
                <Input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="yourname@upi"
                  className="mt-1.5 h-10 border-[#E2E2DF]"
                  data-testid="upi-id"
                />
              </div>
              <Button
                onClick={handleSavePayment}
                disabled={saving}
                className="w-full h-10 bg-[#ef785b] hover:bg-[#D44017] text-white"
                data-testid="save-payment"
              >
                {saving ? 'Saving...' : 'Save Payment Details'}
              </Button>
              <p className="text-xs text-[#64748B]">
                Your payment information is encrypted and secure. Commissions are paid monthly.
              </p>
            </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="mt-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="w-5 h-5 text-[#ef785b]" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-[#64748B] mb-3">Choose how you hear about each kind of update. Account and security alerts are always on.</p>
              <div className="flex items-center justify-end gap-6 px-3 pb-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#64748B]">In app</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#64748B]">Email</span>
              </div>
              <div className="space-y-3">
                {/* Always-on account row */}
                <div className="flex items-center justify-between p-3 bg-[#FAFAF8] rounded-lg">
                  <div>
                    <p className="font-medium text-sm text-[#1A1A1A]">Account &amp; Security</p>
                    <p className="text-xs text-[#64748B]">Approval, suspension and critical alerts</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <Switch checked disabled />
                    <Switch checked disabled />
                  </div>
                </div>
                {MUTABLE_CATEGORIES.map((category) => {
                  const meta = getCategoryMeta(category);
                  const enabled = prefs[category] !== false; // default on
                  const emailEnabled = emailPrefs[category] !== false; // default on
                  return (
                    <div key={category} className="flex items-center justify-between p-3 bg-[#FAFAF8] rounded-lg">
                      <div>
                        <p className="font-medium text-sm text-[#1A1A1A]">{meta.label}</p>
                        <p className="text-xs text-[#64748B]">{CATEGORY_DESC[category] || ''}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <Switch
                          checked={enabled}
                          disabled={savingPref === category}
                          onCheckedChange={(val) => handleTogglePref(category, val)}
                          aria-label={`${meta.label} in-app notifications`}
                        />
                        <Switch
                          checked={emailEnabled}
                          disabled={savingPref === `email:${category}`}
                          onCheckedChange={(val) => handleToggleEmailPref(category, val)}
                          aria-label={`${meta.label} emails`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PartnerProfile;
